import React, { useCallback, useEffect } from 'react';
import { HtmlSanitized } from 'components/display/HtmlSanitized';
import { useDialog } from "hooks/useDialog";
import { GuaranteeDto } from 'api/pricing';
import GenericTabs from 'components/navigation/Tabs/Tabs';
import { Button } from 'components/ui/Button';
import { Sizes } from 'components/forms/NumberSelector';
import { GuaranteeCardItem } from './GuaranteeCardItem';
import { useGuaranteeFilter } from '../../hooks/useGuaranteeFilter';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { NsKey } from 'enums/NsKey';
import { GuaranteesReferential } from '../../types/interfaces';

interface GuaranteeFilterProps {
  isOptional: boolean;
  /** Mode bien : sélection initiale de l'override (défaut : sélection globale). */
  initialGuarantees?: GuaranteesReferential;
  /** Mode bien : reçoit la sélection au « Valider » (met à jour l'override du bien). */
  onApply?: (selected: GuaranteesReferential) => void;
  /** Mode bien : retour à la liste des biens. */
  onBack?: () => void;
}

const GuaranteeFilter: React.FC<GuaranteeFilterProps> = ({ isOptional, initialGuarantees, onApply, onBack }) => {
  const { openDialog, setDialogActions } = useDialog();
  const { t } = useDynamicTranslation([NsKey.Pricing, NsKey.Common])

  const isBienMode = !!onApply;

  const {
    currentTab,
    setCurrentTab,
    globalGuaranteesReferential,
    hasOptionalGuarantees,
    selectedCount,
    onSelectAll,
    onDeselectAllOptional,
    isActive,
    handleToggleGuarantee,
    GuaranteeFilterTab,
    updateDeductible,
    updateCeiling,
    getGuaranteeValue,
    handleSubmit,
  } = useGuaranteeFilter({ isOptional, initialGuarantees, onApply });

  // Mode bien : on pilote le pied du dialog (« Valider » applique + retour à la liste).
  // Le « Retour aux biens » (en haut) sert d'annulation. En mode global, le hook gère le panel.
  useEffect(() => {
    if (!isBienMode) return;
    setDialogActions({
      submitLabel: "Valider",
      onSubmit: () => {
        handleSubmit();
        onBack?.();
      },
    });
  }, [isBienMode, handleSubmit, onBack, setDialogActions]);

  const showGuaranteeDetails = useCallback(
    (title: string, longDescription: string) => {
      openDialog({
        title,
        size: Sizes["4xl"],
      },
        <HtmlSanitized html={longDescription} className="font-12 text-dark-light w-100" />
      );
    }, [openDialog]
  );

  const renderGuaranteeCard = useCallback((guarantee: GuaranteeDto) => {
    const values = getGuaranteeValue(guarantee.id);

    return (
      <GuaranteeCardItem
        key={guarantee.name}
        isActive={isActive(guarantee.id)}
        guarantee={guarantee}
        isRequired={guarantee.isRequired}
        toggleGuarantee={() => !guarantee.isRequired && handleToggleGuarantee(guarantee)}
        showGuaranteeDetails={showGuaranteeDetails}
        deductible={{ value: values.deductible ?? 0, parameters: guarantee.deductible?.parameters }}
        ceiling={{ value: values.ceiling ?? 0, parameters: guarantee.ceiling?.parameters }}
        onDeductibleChange={(value) => updateDeductible(guarantee.id, value)}
        onCeilingChange={(value) => updateCeiling(guarantee.id, value)}
      />
    );
  }, [isActive, showGuaranteeDetails, getGuaranteeValue, updateDeductible, updateCeiling, handleToggleGuarantee]);

  const renderGuarantees = useCallback((guarantees: GuaranteeDto[]) => {
    return (
      <>
        <p className='text-sm text-primary mb-4'>
          {t('PRICING.GUARANTEES.PANEL_MESSAGE_INFO')}
        </p>
        <div className='flex justify-between mb-4 h-10'>
          <div className="flex items-center gap-3">
            <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
              {selectedCount}/{guarantees.length} sélectionnée{selectedCount > 1 ? 's' : ''}
            </span>
          </div>
          {currentTab === GuaranteeFilterTab.Optional && (
            <div className="flex gap-2">
              <Button
                outline
                onClick={onDeselectAllOptional}
              >
                {t('COMMON.DESELECT_ALL')}
              </Button>
              <Button
                color='secondary'
                onClick={onSelectAll}
              >
                {t('COMMON.SELECT_ALL')}
              </Button>
            </div>
          )}
        </div>
        <div className='flex flex-col gap-4 p-4 rounded-2xl bg-section'>
          {guarantees.map(renderGuaranteeCard)}
        </div>
      </>
    );
  }, [
    selectedCount,
    t,
    currentTab,
    GuaranteeFilterTab.Optional,
    onDeselectAllOptional,
    onSelectAll,
    renderGuaranteeCard
  ]);

  return (
    <div className="mb-8">
      {isBienMode && (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4"
        >
          ← Retour au parc immobilier
        </button>
      )}

      <div className="flex items-center justify-between mb-4">
        {hasOptionalGuarantees && (
          <GenericTabs defaultTab={currentTab} onChange={setCurrentTab}>
            <GenericTabs.Item label="Garanties obligatoires" value={GuaranteeFilterTab.Mandatory} />
            <GenericTabs.Item label="Garanties optionnelles" value={GuaranteeFilterTab.Optional} />
            <GenericTabs.Content value={GuaranteeFilterTab.Mandatory}>
              {renderGuarantees(globalGuaranteesReferential.mandatory)}
            </GenericTabs.Content>
            <GenericTabs.Content value={GuaranteeFilterTab.Optional}>
              {renderGuarantees(globalGuaranteesReferential.optional)}
            </GenericTabs.Content>
          </GenericTabs>
        )}
        {!hasOptionalGuarantees && (
          <div className='w-full'>
            {renderGuarantees(globalGuaranteesReferential.mandatory)}
          </div>
        )}
      </div>
    </div>
  );
};

export default GuaranteeFilter;
